import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowRight, Sparkles, FileText, Shield, Download,
  Target, BarChart3, Briefcase, CheckCircle2, Quote,
  Landmark, Lightbulb, TrendingUp, Rocket
} from "lucide-react";

const features = [
  { icon: Sparkles, title: "AI-drevet innhold", desc: "Få et profesjonelt utkast på minutter, skreddersydd for din idé og bransje." },
  { icon: FileText, title: "Komplett struktur", desc: "Sammendrag, SWOT, marked, budsjett og handlingsplan - klar til bruk." },
  { icon: Target, title: "Tilpasset Norge", desc: "Tilpasset ENK, AS, og søknader til Innovasjon Norge eller banken din." },
  { icon: BarChart3, title: "Enkelt budsjett", desc: "Estimater og forutsetninger som hjelper deg å forstå tallene dine." },
  { icon: Download, title: "PDF-eksport", desc: "Last ned en ferdig formatert PDF du kan dele med investorer eller bank." },
  { icon: Shield, title: "Trygg og privat", desc: "Dine data lagres trygt og deles aldri uten ditt samtykke." },
];

const steps = [
  { n: "01", title: "Beskriv ideen din", desc: "Svar på korte spørsmål om idé, marked og mål." },
  { n: "02", title: "Få planen generert", desc: "Vår norske AI bygger en fullstendig forretningsplan." },
  { n: "03", title: "Lås opp og last ned", desc: "Betal én gang, lås opp full rapport og PDF-eksport." },
];

const testimonials = [];

const whyChoose = [
  { icon: "🇳🇴", title: "Laget for norske gründere", desc: "Tilpasset ENK, AS og norske søknadsprosesser fra bunn." },
  { icon: "📄", title: "Eksport til PDF", desc: "Last ned en ferdig formatert plan - klar for bank eller investor." },
  { icon: "💡", title: "SWOT, budsjett og handlingsplan", desc: "Alle byggesteinene en profesjonell rapport trenger." },
  { icon: "⚡", title: "Ferdig på ca. 10 minutter", desc: "Fra idé til komplett plan mens kaffen fortsatt er varm." },
];

const faqs = [
  { q: "Hvem passer IdeSetGo for?", a: "IdeSetGo passer for gründere, frilansere, småbedriftseiere og alle som ønsker å starte et ENK eller AS, søke finansiering eller bare strukturere ideen sin." },
  { q: "Trenger jeg å være ekspert for å bruke det?", a: "Nei. Du fyller bare ut korte spørsmål på norsk - så gjør AI-en resten. Du kan alltid redigere senere." },
  { q: "Hva får jeg i den fulle rapporten?", a: "Sammendrag, forretningsidé, målgruppe, problem/løsning, inntektsmodell, SWOT, markedsføringsplan, handlingsplan og enkel budsjettoversikt - alt i én PDF." },
  { q: "Hvordan fungerer betalingen?", a: "Vi bruker Stripe for sikker betaling. Du betaler kun 299 NOK én gang per forretningsplan - ingen abonnement." },
  { q: "Kan jeg redigere innholdet etterpå?", a: "Ja. Du kan justere svarene og generere planen på nytt så mange ganger du vil før du betaler for opplåsing." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const goStart = () => navigate(user ? "/dashboard" : "/register");

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-20 pb-24 sm:pt-28 sm:pb-32 relative">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 fade-up">
              <Badge variant="secondary" className="rounded-full px-3 py-1 bg-white border border-gray-200 text-slate-700 mb-6" data-testid="hero-badge">
                <Sparkles className="h-3.5 w-3.5 mr-1.5" /> Drevet av Claude Sonnet 4.5
              </Badge>
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-medium tracking-tight text-slate-900 leading-[1.05]">
                Forretningsplan, <br className="hidden sm:block" />
                <span className="italic font-normal text-slate-500">ferdig på minutter.</span>
              </h1>
              <p className="mt-6 text-lg sm:text-xl text-slate-600 max-w-xl leading-relaxed">
                En forretningsplan tilpasset det du faktisk skal bruke den til. Beskriv idéen — så bygger IdeSetGo en profesjonell, norsk plan med prioriteringer som passer bank, Innovasjon Norge, investor eller din egen oppstart.
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-3">
                <Button
                  size="lg"
                  onClick={goStart}
                  className="rounded-full bg-slate-900 hover:bg-slate-800 text-white px-7 h-12 text-base"
                  data-testid="hero-start-btn"
                >
                  Lag planen min <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full px-7 h-12 text-base border-gray-300"
                  data-testid="hero-example-btn"
                >
                  <Link to="/eksempler">Se eksempel rapport</Link>
                </Button>
              </div>
              <div className="mt-10 flex items-center gap-6 text-sm text-slate-500">
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Gratis utkast</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Ingen abonnement</div>
                <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> 100% norsk</div>
              </div>
            </div>
            <div className="lg:col-span-5 fade-up">
              <div className="relative">
                <div className="absolute -inset-6 bg-gradient-to-br from-slate-200/40 to-transparent rounded-3xl blur-2xl" />
                <Card className="relative bg-white border border-gray-200 rounded-2xl shadow-xl overflow-hidden">
                  <div className="p-6 border-b border-gray-100 flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-red-400"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-yellow-400"></span>
                    <span className="h-2.5 w-2.5 rounded-full bg-emerald-400"></span>
                    <span className="text-xs text-slate-400 ml-3">forretningsplan.pdf</span>
                  </div>
                  <CardContent className="p-8 space-y-5">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Sammendrag</p>
                      <p className="mt-2 text-slate-800 leading-relaxed">
                        Lykke Kaffe vil bli Bergens fremste spesialitetsbar med fokus på lokale råvarer og en sterk lokalsamfunnsprofil...
                      </p>
                    </div>
                    <div className="h-px bg-gray-100" />
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">SWOT</p>
                      <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
                        <div className="rounded-lg bg-emerald-50 border border-emerald-100 p-3"><p className="font-medium text-emerald-800">Styrker</p><p className="text-emerald-700 text-xs mt-1">Sterk merkevare</p></div>
                        <div className="rounded-lg bg-amber-50 border border-amber-100 p-3"><p className="font-medium text-amber-800">Muligheter</p><p className="text-amber-700 text-xs mt-1">Voksende marked</p></div>
                        <div className="rounded-lg bg-rose-50 border border-rose-100 p-3"><p className="font-medium text-rose-800">Svakheter</p><p className="text-rose-700 text-xs mt-1">Begrenset kapital</p></div>
                        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3"><p className="font-medium text-slate-800">Trusler</p><p className="text-slate-700 text-xs mt-1">Konkurranse</p></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section id="bruksomrader" className="py-20 sm:py-24 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Bruksområder</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              En forretningsplan tilpasset <br className="hidden sm:block" />
              <span className="italic font-normal text-slate-500">det du faktisk skal bruke den til.</span>
            </h2>
            <p className="mt-5 text-lg text-slate-600">
              Velg formål i første steg. IdeSetGo justerer prioriteringer, dybde og språktone deretter — men lager fortsatt en komplett plan.
            </p>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="landing-use-cases">
            {[
              { icon: Landmark, title: "Bank og finansiering", body: "Kapitalbehov, budsjett, likviditet og tilbakebetalingsevne løftes." },
              { icon: Lightbulb, title: "Innovasjon Norge", body: "Innovasjon, markedspotensial, verdiskaping og gjennomføringsevne." },
              { icon: TrendingUp, title: "Investor", body: "Marked, forretningsmodell, skalerbarhet og vekststrategi." },
              { icon: Rocket, title: "Egen oppstart", body: "Konkrete neste steg, prising, første kunder og 12-mnd handlingsplan." },
            ].map(({ icon: Icon, title, body }) => (
              <div key={title} className="p-6 rounded-2xl border border-gray-200 bg-white hover:border-slate-300 transition-colors" data-testid={`landing-use-case-${title.toLowerCase().replace(/\s+/g,'-')}`}>
                <div className="h-10 w-10 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Icon className="h-5 w-5 text-slate-700" />
                </div>
                <p className="mt-4 font-medium text-slate-900">{title}</p>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section id="funksjoner" className="py-24 sm:py-32 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Funksjoner</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">
              Alt du trenger i én plan.
            </h2>
            <p className="mt-5 text-lg text-slate-600">Vi har samlet de viktigste byggesteinene i en moderne forretningsplan - tilpasset norske gründere.</p>
          </div>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <Card key={i} className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-testid={`feature-card-${i}`}>
                <CardContent className="p-8">
                  <div className="h-10 w-10 rounded-xl bg-slate-900 text-white flex items-center justify-center mb-5">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-xl font-medium tracking-tight text-slate-900">{f.title}</h3>
                  <p className="mt-3 text-slate-600 leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="slik-fungerer-det" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Slik fungerer det</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">Fra idé til ferdig plan i tre steg.</h2>
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-6">
            {steps.map((s, i) => (
              <div key={i} className="rounded-2xl p-8 bg-white border border-gray-200" data-testid={`step-${i}`}>
                <div className="text-sm text-slate-400 tracking-widest">{s.n}</div>
                <h3 className="mt-4 text-2xl font-medium tracking-tight text-slate-900">{s.title}</h3>
                <p className="mt-3 text-slate-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE IDESETGO */}
      <section className="py-24 sm:py-32 bg-white border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Hvorfor IdeSetGo</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">Hvorfor velger gründere IdeSetGo?</h2>
            <p className="mt-5 text-lg text-slate-600">Bygget for norske forhold - med alle byggesteinene banken, investoren eller Innovasjon Norge forventer.</p>
          </div>
          <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyChoose.map((w, i) => (
              <Card key={i} className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300" data-testid={`why-card-${i}`}>
                <CardContent className="p-8">
                  <div className="text-3xl leading-none" aria-hidden>{w.icon}</div>
                  <h3 className="mt-5 text-lg font-medium tracking-tight text-slate-900">{w.title}</h3>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{w.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section id="priser" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Priser</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">Enkel, ærlig pris.</h2>
            <p className="mt-5 text-lg text-slate-600">Ingen abonnement. Betal kun for det du trenger.</p>
          </div>
          <div className="mt-16 grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            <Card className="bg-white border border-gray-200 rounded-2xl">
              <CardContent className="p-10">
                <p className="text-sm font-medium text-slate-500">Gratis</p>
                <p className="mt-3 text-5xl font-medium tracking-tight">0 kr</p>
                <p className="mt-2 text-sm text-slate-500">For å komme i gang</p>
                <ul className="mt-8 space-y-3 text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> Fyll ut spørreskjema</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> AI-generert utkast</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> Forhåndsvisning av sammendrag</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> Lagre planer på kontoen din</li>
                </ul>
                <Button onClick={goStart} variant="outline" className="mt-10 w-full rounded-full h-11" data-testid="pricing-free-btn">Kom i gang gratis</Button>
              </CardContent>
            </Card>
            <Card className="bg-slate-900 border border-slate-900 rounded-2xl text-white relative overflow-hidden">
              <div className="absolute top-4 right-4">
                <Badge className="bg-white text-slate-900 hover:bg-white">Mest populær</Badge>
              </div>
              <CardContent className="p-10">
                <p className="text-sm font-medium text-slate-300">Full rapport</p>
                <p className="mt-3 text-5xl font-medium tracking-tight">299 kr</p>
                <p className="mt-2 text-sm text-slate-400">Engangsbeløp - lås opp én plan</p>
                <ul className="mt-8 space-y-3 text-slate-100">
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" /> Alt i Gratis-planen</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" /> Full forretningsplan med alle seksjoner</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" /> SWOT, budsjett og handlingsplan</li>
                  <li className="flex gap-2"><CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" /> PDF-eksport klar for bank/Innovasjon Norge</li>
                </ul>
                <Button onClick={goStart} className="mt-10 w-full rounded-full h-11 bg-white text-slate-900 hover:bg-slate-100" data-testid="pricing-pro-btn">Lås opp full rapport</Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-24 sm:py-32 bg-white border-y border-gray-100">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">FAQ</p>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-slate-900">Spørsmål & svar</h2>
          </div>
          <Accordion type="single" collapsible className="mt-12">
            {faqs.map((f, i) => (
              <AccordionItem value={`item-${i}`} key={i} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left text-lg">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 sm:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="rounded-3xl bg-slate-900 text-white p-12 sm:p-20 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 h-64 w-64 rounded-full bg-white/5 blur-3xl"></div>
            <p className="text-sm font-medium tracking-widest uppercase text-slate-300"><Briefcase className="h-4 w-4 inline mr-2" /> Klar til å starte?</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight">Din neste forretning starter med en plan.</h2>
            <p className="mt-5 text-lg text-slate-300 max-w-2xl">Bli med tusenvis av norske gründere som har strukturert idéen sin med IdeSetGo. Det tar kun 10 minutter.</p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Button size="lg" onClick={goStart} className="rounded-full h-12 px-7 bg-white text-slate-900 hover:bg-slate-100" data-testid="cta-final-btn">
                Lag min forretningsplan <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              <Link to="/login" className="inline-flex items-center justify-center rounded-full h-12 px-7 border border-white/20 text-white hover:bg-white/10 transition-colors" data-testid="cta-login-btn">
                Logg inn
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
